import React, { useEffect, useState } from "react";
import {
  DISTRIBUTION_DATE,
  DISTRIBUTION_DESCRIPTION_ID,
  DISTRIBUTION_DESCRIPTION_ID_FRENCH,
  DISTRIBUTION_ID,
  DISTRIBUTION_ID_FRENCH,
} from "../constants/Constants";
import { DistributionSetupFormType } from "../constants/type";
import { useController, useForm } from "react-hook-form";
import ControlInputBox from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import ControlTextAreaWithCharLimit from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import Button from "@/components/Form/Button/Button";
import { maxPageSize, SECONDARY } from "@/Constant";
import { useTranslation } from "react-i18next";
import {
  MotifErrorMessage,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateDistributionMutation,
  useCreateDuplicateDistributionMutation,
  useGetDistributionByCaseIDQuery,
} from "@/services/distribution/distributionReducers";
import FormattedDatePicker from "@/components/FormattedDatePicker/FormattedDatePicker";
import { useDispatch } from "react-redux";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import { getOnlyDate } from "@/utility/common";
import { ActiveTabsOrder } from "@/menuData";
import CheckBox from "@/components/Form/CheckBox/CheckBox";

interface DistributionSetupFormProps {
  isDuplicate: boolean;
  distributionId: string | undefined;
}

const DistributionSetupForm = ({
  isDuplicate,
  distributionId,
}: DistributionSetupFormProps) => {
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();
  const [duplicateDistribution, setDuplicateDistribution] = useState(
    false || isDuplicate
  );
  const [showDuplicateCheckBox, setShowDuplicateCheckBox] = useState(
    false || isDuplicate
  );
  const [duplicateDistributionId, setDuplicateDistributionId] = useState<
    string | undefined
  >(distributionId);
  const {
    data,
    isLoading: distributionListLoading,
    isFetching,
    error,
  } = useGetDistributionByCaseIDQuery(
    {
      CaseId: caseId,
      PageNumber: 1,
      PageSize: maxPageSize,
      SearchText: "",
      SortOrder: "desc",
      SortColumn: "DistributionDate",
    },
    { skip: isDuplicate }
  );
  useEffect(() => {
    if (data) {
      data?.results?.find((item: any) => {
        if (item.isAllowToReplicate) {
          setDuplicateDistributionId(item.id);
          setShowDuplicateCheckBox(true);
          return true;
        }
        return false;
      });
    }
  }, [data, setDuplicateDistributionId, setShowDuplicateCheckBox]);
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<DistributionSetupFormType>({
    defaultValues: {
      [DISTRIBUTION_ID]: "",
      [DISTRIBUTION_ID_FRENCH]: "",
      [DISTRIBUTION_DESCRIPTION_ID]: "",
      [DISTRIBUTION_DESCRIPTION_ID_FRENCH]: "",
      [DISTRIBUTION_DATE]: new Date().toISOString(),
    },
  });
  const { field: distributionDateField } = useController({
    name: "distributionDate",
    control,
    rules: {
      required: {
        value: true,
        message: t(
          "pages.distributions.distribution.validationMessages.distributionDateReq"
        ),
      },
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
    message: "",
  });
  const closeMessageHandler = () => {
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };
  const renderMessage = () => {
    if (showMessage.show) {
      window.scrollTo(0, 0);
      return (
        <MotifToast
          onClose={closeMessageHandler}
          //@ts-ignore
          variant={showMessage.type}
        >
          {" "}
          {showMessage.type === "error"
            ? t(showMessage.message)
            : t("global.globalNotifications.updatedSuccessfully")}
        </MotifToast>
      );
    }
  };
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: isDirty }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, isDirty]);
  const navigate = useNavigate();
  const [createDistribution] = useCreateDistributionMutation();
  const [createDuplicateDistribution] =
    useCreateDuplicateDistributionMutation();
  const onSubmit = async (data: DistributionSetupFormType) => {
    setIsLoading(true);
    const requestBody = duplicateDistribution
      ? {
          distributionId: duplicateDistributionId,
          name: data.name,
          description: data.description,
          nameFrench: data.nameFrench,
          descriptionFrench: data.descriptionFrench,
          distributionDate: data?.distributionDate
            ? getOnlyDate(data?.distributionDate)
            : null,
        }
      : {
          caseId,
          name: data.name,
          description: data.description,
          nameFrench: data.nameFrench,
          descriptionFrench: data.descriptionFrench,
          isActive: true,
          IsDistributionCreationFromScratch: true,
          distributionDate: data?.distributionDate
            ? getOnlyDate(data?.distributionDate)
            : null,
        };
    dispatch(setDiscardEditMode({ editMode: false }));
    if (duplicateDistribution) {
      await createDuplicateDistribution(requestBody).then((response: any) => {
        if (response.data) {
          navigate(`/case/${caseId}/distribution/${response.data}`, {
            state: {
              showMessage: true,
              message: response.data.message,
              type: "success",
            },
          });
        } else {
          setShowMessage({
            ...showMessage,
            show: true,
            type: "error",
            message:
              response.error?.data?.message ||
              "global.globalNotifications.unExpectedError",
          });
          setIsLoading(false);
          dispatch(setDiscardEditMode({ editMode: true }));
        }
      });
    } else {
      await createDistribution(requestBody).then((response: any) => {
        if (response?.data) {
          navigate(
            `/case/${caseId}/distribution/${response.data.distributionId}`,
            {
              state: {
                showMessage: true,
                message:
                  response.data?.message ||
                  t("global.globalNotifications.createdSuccessfully"),
                type: "success",
              },
            }
          );
        } else {
          setShowMessage({
            ...showMessage,
            show: true,
            type: "error",
            message:
              response.error?.data?.message ||
              "global.globalNotifications.unExpectedError",
          });
          setIsLoading(false);
          dispatch(setDiscardEditMode({ editMode: true }));
        }
      });
    }
  };
  if (distributionListLoading || isFetching) {
    return <MotifProgressLoader show variant="default" className="mar-b-20" />;
  }
  if (error) {
    return (
      <MotifErrorMessage>
        {t("global.globalNotifications.wentWrong")}
      </MotifErrorMessage>
    );
  }

  return (
    <>
      {renderMessage()}
      <form onSubmit={handleSubmit(onSubmit)} className="mar-b-80">
        <div className="motif-row">
          <div className="motif-col-md-3 ">
            <div className="form-field ">
              <ControlInputBox
                name={DISTRIBUTION_ID}
                control={control}
                required={true}
                isTextArea={false}
                label={
                  "pages.distributions.distribution.distributionFormLabels.distributionName"
                }
                showLabel={true}
              />
              {errors[DISTRIBUTION_ID] &&
                errors[DISTRIBUTION_ID].type === "required" && (
                  <MotifErrorMessage>
                    {t(
                      "pages.distributions.distribution.validationMessages.distributionNameReq"
                    )}{" "}
                  </MotifErrorMessage>
                )}
            </div>
          </div>
          <div className="motif-col-md-3">
            <div className="form-field">
              <ControlInputBox
                name={DISTRIBUTION_ID_FRENCH}
                control={control}
                isTextArea={false}
                label={
                  "pages.distributions.distribution.distributionFormLabels.distributionNameFrench"
                }
                showLabel={true}
              />
            </div>
          </div>
        </div>
        <br></br>

        <div className="motif-row next-row">
          <div className="motif-col-md-3">
            <div className="form-field">
              <ControlTextAreaWithCharLimit
                name={DISTRIBUTION_DESCRIPTION_ID}
                control={control}
                isTextArea={true}
                label={
                  "pages.distributions.distribution.distributionFormLabels.description"
                }
                showLabel={true}
              />
            </div>
          </div>
          <div className="motif-col-md-3">
            <div className="form-field ">
              <ControlTextAreaWithCharLimit
                name={DISTRIBUTION_DESCRIPTION_ID_FRENCH}
                control={control}
                isTextArea={true}
                label={
                  "pages.distributions.distribution.distributionFormLabels.descriptionFrench"
                }
                showLabel={true}
              />
            </div>
          </div>
        </div>
        <div className="motif-row next-row">
          <div className="motif-col-md-3">
            <div className="form-field ">
              <div className="formFieldLabel">
                <label htmlFor="input" aria-label={"Distribution Date"}>
                  {t(
                    "pages.distributions.distribution.distributionFormLabels.distributionDate"
                  )}
                  <span className="requiredSymbol">*</span>
                </label>
              </div>
              <FormattedDatePicker
                onChange={(value) => {
                  distributionDateField.onChange(value);
                }}
                required={true}
                value={distributionDateField.value}
                minDate={new Date()}
              />
              {errors.distributionDate &&
                errors.distributionDate.type === "required" && (
                  <MotifErrorMessage>
                    {t(errors.distributionDate.message || "")}
                  </MotifErrorMessage>
                )}
            </div>
          </div>
        </div>
        {showDuplicateCheckBox && (
          <>
            <br></br>
            <div className="motif-row next-row">
              <div className="motif-col-md-4">
                <CheckBox
                  value={duplicateDistribution}
                  checked={duplicateDistribution}
                  name="duplicateDistribution"
                  onChange={() =>
                    setDuplicateDistribution(!duplicateDistribution)
                  }
                  label={t(
                    "pages.distributions.distribution.distributionFormLabels.duplicateDistributionCheckBox"
                  )}
                />
              </div>
            </div>
          </>
        )}
        <div className="button-container sticky-footer">
          <Button
            label={t("global.globalActions.save")}
            type={isLoading ? "loader" : "submit"}
            disabled={isLoading}
          />
          <Button
            type="button"
            label={t("global.globalActions.cancel")}
            variant={SECONDARY}
            className="cancelBtn"
            onClickHandler={() => {
              navigate(
                `/case/${caseId}/?activeTab=${ActiveTabsOrder.DISTRIBUTION}`
              );
            }}
          />
        </div>
      </form>
    </>
  );
};

export default DistributionSetupForm;
